import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Award, BookOpen, Users, Shield, Heart, Lightbulb, Eye, Globe2 } from 'lucide-react';
import axios from 'axios';
import { useSiteContent } from '../contexts/SiteContentContext';

const API = process.env.REACT_APP_BACKEND_URL;
const B = `${API}/api/files/dersimizcografiya/uploads`;
const LOCAL = {
  teacher: `${B}/7e4162ab-1113-4416-a3c9-767c9de771a1.jpg`,
  river: `${B}/04018507-b98a-404d-bd0d-1e06437f063d.jpg`,
  hero_bg: `${B}/d6ce482f-a305-43fa-b24a-98d96d8686d5.jpg`,
};

const iconMap = { GraduationCap, Award, BookOpen, Users, Shield, Heart, Lightbulb, Eye };

export default function About() {
  const content = useSiteContent();
  
  const c = content || {};

  if (!content) return <div className="min-h-screen flex items-center justify-center bg-[#F4F7F6]"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;

  const pg = c.about_page || {};
  // Hero
  const heroBadge = pg.hero_badge || 'Haqqımızda';
  const heroTitle = pg.hero_title || 'Biz Kimik?';
  const heroSubtitle = pg.hero_subtitle || 'Peşəkar coğrafiya müəllimi olaraq şagird və tələbələrə keyfiyyətli təhsil xidməti göstəririk';
  const heroBg = pg.hero_bg || LOCAL.hero_bg;
  // Teacher
  const tBadge = pg.teacher_badge || 'Müəllim';
  const tName = pg.teacher_name || 'Coğrafiya Müəllimi';
  const tDesc1 = pg.teacher_desc1 || 'Uzun illər ərzində coğrafiya sahəsində təhsil verərək yüzlərlə şagirdin uğur qazanmasına kömək etmişəm. Müasir təhsil metodları, interaktiv dərslər və fərdi yanaşma ilə hər bir şagirdin potensialını üzə çıxarmağa çalışıram.';
  const tDesc2 = pg.teacher_desc2 || 'Bakı Dövlət Universiteti Coğrafiya fakültəsini bitirmişəm. Fiziki coğrafiya, iqtisadi coğrafiya, xəritəşünaslıq və ətraf mühitin mühafizəsi sahələrində dərin biliyə sahibəm.';
  const tImg = pg.teacher_image || LOCAL.teacher;
  const tExp = pg.teacher_exp || '15+';
  const tExpLabel = pg.teacher_exp_label || 'İl Təcrübə';
  const tEdu = pg.teacher_edu || 'BDU - Coğrafiya';
  const tSpec = pg.teacher_spec || 'Coğrafiya Müəllimi';
  // Highlights
  const defHL = [
    { icon: 'GraduationCap', title: 'Peşəkar Təhsil', desc: 'BDU Coğrafiya fakültəsi məzunu' },
    { icon: 'Award', title: '15+ İl Təcrübə', desc: 'Uzun illər pedaqoji fəaliyyət' },
    { icon: 'BookOpen', title: '50+ Kurs', desc: 'Müxtəlif mövzularda dərs proqramları' },
    { icon: 'Users', title: '500+ Şagird', desc: 'Uğurla hazırlanmış tələbə və şagirdlər' },
  ];
  const highlights = pg.highlights || defHL;
  const hlIcons = [GraduationCap, Award, BookOpen, Users];
  // Mission
  const mImg = pg.mission_image || LOCAL.river;
  const mBadge = pg.mission_badge || 'Missiya';
  const mTitle = pg.mission_title || 'Bizim Missiyamız';
  const mText = pg.mission_text || 'Müştərilərimizi - şagird və tələbələri məmnun etmək üçün ən keyfiyyətli təhsil materiallarını təqdim edərək, yerli təhsil sektorunun inkişafına töhfə vermək.';
  const vBadge = pg.vision_badge || 'Vizyon';
  const vTitle = pg.vision_title || 'Bizim Vizyonumuz';
  const vText = pg.vision_text || 'Azərbaycanda coğrafiya təhsili sahəsinin inkişafına töhfə verən, tələbə və şagirdlər üçün ən etibarlı təhsil platforması olmaq.';
  // Values
  const valBadge = pg.values_badge || 'Dəyərlərimiz';
  const valTitle = pg.values_title || 'Bizim Dəyərlərimiz';
  const valSubtitle = pg.values_subtitle || 'Bizim işimizdə əsas qalan dəyərlər';
  const defVals = [
    { title: 'Keyfiyyət və etibarlılıq' }, { title: 'Şagird məmnuniyyəti' },
    { title: 'Peşəkarlıq' }, { title: 'İnnovasiya' }, { title: 'Şəffaflıq' },
  ];
  const values = pg.values || defVals;
  const valIcons = [Shield, Heart, Award, Lightbulb, Eye];

  return (
    <div>
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#03045E]/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-bold mb-4 border border-white/20"><Globe2 className="w-4 h-4" /> {heroBadge}</span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mt-4">{heroTitle}</h1>
            <p className="text-white font-semibold text-base md:text-lg max-w-2xl mx-auto mt-6">{heroSubtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Teacher */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-teacher-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <motion.div className="lg:col-span-2" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl"><img src={tImg} alt="Müəllim" className="w-full h-[420px] object-cover" /></div>
                <div className="absolute -bottom-6 -right-6 bg-[#0077B6] text-white rounded-2xl p-6 shadow-xl">
                  <div className="text-3xl font-bold">{tExp}</div>
                  <div className="text-xs mt-1 text-white/80">{tExpLabel}</div>
                </div>
              </div>
            </motion.div>
            <motion.div className="lg:col-span-3" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="inline-block px-3 py-1 rounded-full bg-[#0077B6]/10 text-[#0077B6] text-sm font-bold mb-3 border border-[#0077B6]/20">{tBadge}</span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1A2530] mt-2 mb-6">{tName}</h2>
              <p className="text-[#5C6B7A] leading-relaxed mb-4">{tDesc1}</p>
              <p className="text-[#5C6B7A] leading-relaxed mb-8">{tDesc2}</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F4F7F6] rounded-xl p-4 border border-[#E2E8F0]"><div className="text-xs text-[#5C6B7A]">Təhsil</div><div className="font-semibold text-[#1A2530] mt-1">{tEdu}</div></div>
                <div className="bg-[#F4F7F6] rounded-xl p-4 border border-[#E2E8F0]"><div className="text-xs text-[#5C6B7A]">İxtisas</div><div className="font-semibold text-[#1A2530] mt-1">{tSpec}</div></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-[#F4F7F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((item, i) => {
              const Icon = iconMap[item.icon] || hlIcons[i] || Award;
              return (
                <motion.div key={i} className="bg-white rounded-2xl p-8 border border-[#E2E8F0] card-hover text-center"
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #0077B620, #00B4D810)' }}>
                    <Icon className="w-7 h-7 text-[#0077B6]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1A2530] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#5C6B7A]">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="rounded-3xl overflow-hidden shadow-xl"><img src={mImg} alt="Missiya" className="w-full h-[380px] object-cover" /></div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="space-y-8">
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-[#0077B6]/10 text-[#0077B6] text-sm font-bold mb-3 border border-[#0077B6]/20">{mBadge}</span>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#1A2530] mb-3">{mTitle}</h3>
                  <p className="text-[#5C6B7A] leading-relaxed">{mText}</p>
                </div>
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-[#0077B6]/10 text-[#0077B6] text-sm font-bold mb-3 border border-[#0077B6]/20">{vBadge}</span>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#1A2530] mb-3">{vTitle}</h3>
                  <p className="text-[#5C6B7A] leading-relaxed">{vText}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#F4F7F6]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-[#0077B6]/10 text-[#0077B6] text-sm font-bold border border-[#0077B6]/20">{valBadge}</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A2530] mt-4">{valTitle}</h2>
            <p className="text-[#5C6B7A] font-semibold mt-3">{valSubtitle}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {values.map((val, i) => {
              const Icon = iconMap[val.icon] || valIcons[i] || Shield;
              const color = '#0077B6';
              return (
                <motion.div key={i} className="bg-white rounded-2xl p-6 border border-[#E2E8F0] text-center card-hover"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `linear-gradient(135deg, ${color}20, ${color}10)` }}>
                    <Icon className="w-7 h-7" style={{ color }} />
                  </div>
                  <h3 className="font-semibold text-[#1A2530] text-sm leading-tight">{val.title}</h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
